import {
  Alert,
  Box,
  ExpandableSection,
  FormField,
  Input,
  Multiselect,
  Select,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';
import {
  DefaultTolerance,
  FailureTagsMap,
  InspectionValues,
  StepProcessMap,
  StepTypes,
} from '@parallel-fluidics/shared/constants';
import { decodePPartId } from '@parallel-fluidics/shared/models/projects/parts';
import FileUploader from 'components/FileUploader';
import PPartDisplay from 'components/Projects/pPartDisplay';
import InspectionStatus from 'features/travelers/components/InspectionStatus';
import PropTypes from 'prop-types';
import React from 'react';

import ProcessDisplay from '../processDisplay';
import summarizeStep from '../utils/summarizeStep';
import StepComponentsField from './StepComponentsField';

function NotesWrapper({ children }) {
  return (
    <Alert statusIconAriaLabel="Warning" type="warning">
      {children}
    </Alert>
  );
}
NotesWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Inspection options for a step: Passed is only offered once every earlier
 * step is Passed (inspection steps don't gate) and nothing else blocks it
 * (thickness out of tolerance, BOM not covered).
 */
function getInspectionOptions(previousSteps, disablePass = false) {
  const shouldDisablePass = disablePass || previousSteps.some(
    (step) => step.inspected !== InspectionValues.PASSED && step.type !== StepTypes.INSPECTION,
  );
  return Object.values(InspectionValues).map((value) => ({
    value,
    label: value,
    disabled: shouldDisablePass && value === InspectionValues.PASSED,
  }));
}

/**
 * One traveler step as an expandable row: the header carries the part,
 * the inspection badge and a one-line summary (see summarizeStep); the
 * body is the full step — part notes, the BOM components scan field, run,
 * inspection result / failure tags / note, molding thickness and the
 * inspection report. Reads and writes the step through Formik's
 * setFieldValue; pending bag scans go up through onPendingChange.
 */
export default function TravelerStepCard({
  step,
  index,
  expanded,
  onExpandedChange,
  previousSteps,
  errors = null,
  pendingLots,
  coverage = null,
  pParts = null,
  project = null,
  projectLoading = false,
  orderStepNote = null,
  orderId = null,
  travelerId,
  setFieldValue,
  onPendingChange,
  onCoverageChange,
}) {
  // Legacy and transferred steps can carry only pPartId (the
  // transfer function never sets pPartDocId) — resolve the doc
  // ID from the loaded parts list so the BOM gate still
  // applies to them. Unresolvable IDs degrade to no gate,
  // same as before doc IDs existed.
  let stepPPartDocId = step.pPartDocId || null;
  if (!stepPPartDocId && step.pPartId && pParts?.length > 0) {
    try {
      const { partId, revision, material } = decodePPartId(step.pPartId);
      stepPPartDocId = pParts.find((p) => p.partId === partId
        && p.revision === revision
        && p.material === material)?.id || null;
    } catch {
      stepPPartDocId = null;
    }
  }
  let thickness = null;
  let thicknessTolerance = null;
  let isSmallerThanMin = false;
  let isGreaterThanMax = false;
  if (step.type === StepTypes.MOLDING && step.pPartId && pParts?.length > 0) {
    const { partId, revision, material } = decodePPartId(step.pPartId);
    const pPart = pParts.find((p) => p.partId === partId
      && p.revision === revision
      && p.material === material);
    if (pPart && pPart.metadata?.Thickness) {
      thickness = parseFloat(pPart.metadata.Thickness);
      thicknessTolerance = parseFloat(
        pPart.metadata?.['Thickness Tolerance'] || DefaultTolerance,
      );

      if (step.minThickness && step.minThickness < thickness - thicknessTolerance) {
        isSmallerThanMin = true;
      }
      if (step.maxThickness && step.maxThickness > thickness + thicknessTolerance) {
        isGreaterThanMax = true;
      }
    }
  }
  return (
    <ExpandableSection
      variant="default"
      expanded={expanded}
      onChange={({ detail }) => onExpandedChange(detail.expanded)}
      headerText={(
        <SpaceBetween direction="horizontal" size="xs">
          <PPartDisplay
            fieldName="name"
            projectDocId={project?.id}
            loading={projectLoading}
            pPartId={step.pPartId}
            pPartDocId={step.pPartDocId}
            fallbackMessage={step.name || step.type}
          />
          {step.pPartId && (
            <Box variant="span" color="text-body-secondary">
              {`(${step.pPartId})`}
            </Box>
          )}
          <InspectionStatus inspected={step.inspected} />
        </SpaceBetween>
      )}
      headerDescription={summarizeStep(step)}
    >
      <SpaceBetween size="xs">
        <PPartDisplay
          fieldName="notes"
          pPartId={step.pPartId}
          pPartDocId={step.pPartDocId}
          projectDocId={project?.id}
          loading={projectLoading}
          fallbackMessage={orderStepNote}
          CustomWrapper={NotesWrapper}
          emptyMessage={(
            <Alert statusIconAriaLabel="Error" type="error">
              No connector information provided
            </Alert>
          )}
          showEmptyText={step.type === StepTypes.CONNECTORIZING}
        />
        <StepComponentsField
          stepIndex={index}
          projectDocId={project?.id}
          pPartDocId={stepPPartDocId}
          installedLots={step.installedLots}
          pendingLots={pendingLots}
          onPendingChange={onPendingChange}
          onCoverageChange={onCoverageChange}
        />
        <div className="even-columns">
          {(step.type === StepTypes.MOLDING || step.type === StepTypes.BONDING) && (
            <FormField
              stretch
              label="Run ID"
              errorText={errors?.runId}
            >
              <ProcessDisplay
                processName={StepProcessMap[step.type]}
                selectedOptions={step.runId}
              />
            </FormField>
          )}
          <FormField
            stretch
            label="Inspection"
            errorText={errors?.inspected}
          >
            <Select
              selectedOption={{ value: step.inspected, label: step.inspected }}
              onChange={({ detail }) => {
                const { value } = detail.selectedOption;
                if (
                  value !== InspectionValues.REJECT_MAJOR
                  && value !== InspectionValues.REJECT_COSMETIC
                ) {
                  setFieldValue(`steps[${index}].failureTags`, []);
                }
                setFieldValue(`steps[${index}].inspected`, value);
              }}
              options={getInspectionOptions(
                previousSteps,
                (isSmallerThanMin || isGreaterThanMax)
                  || (coverage != null && !coverage.covered),
              )}
            />
          </FormField>
          {(
            step.inspected === InspectionValues.REJECT_MAJOR
            || step.inspected === InspectionValues.REJECT_COSMETIC
            || step.failureTags?.length > 0
          ) && !!FailureTagsMap[step.type] && (
          <FormField
            stretch
            label="Failure tags"
          >
            <Multiselect
              selectedOptions={
              step.failureTags?.map((tag) => ({ value: tag, label: tag })) || []
            }
              onChange={({ detail }) => setFieldValue(`steps[${index}].failureTags`, detail.selectedOptions?.map((option) => option.value) || [])}
              options={FailureTagsMap[step.type].map((value) => ({
                value,
                label: value,
              }))}
            />
          </FormField>
          )}
        </div>
        {step.type === StepTypes.MOLDING && (
          <div className="even-columns">
            <FormField
              label="Min Thickness (mm)"
              errorText={errors?.minThickness}
              warningText={isSmallerThanMin ? 'Min thickness is outside of the tolerance' : null}
              constraintText={thickness ? `Tolerance: ${thickness} ± ${thicknessTolerance} mm` : null}
            >
              <Input
                inputMode="decimal"
                value={step.minThickness}
                onChange={({ detail }) => setFieldValue(`steps[${index}].minThickness`, detail.value)}
              />
            </FormField>
            <FormField
              label="Max Thickness (mm)"
              errorText={errors?.maxThickness}
              warningText={isGreaterThanMax ? 'Max thickness is outside of the tolerance' : null}
            >
              <Input
                inputMode="decimal"
                value={step.maxThickness}
                onChange={({ detail }) => setFieldValue(`steps[${index}].maxThickness`, detail.value)}
              />
            </FormField>
          </div>
        )}
        <FormField
          label="Inspection Note"
          errorText={errors?.notes}
          stretch
        >
          <Textarea
            value={step.inspectedNote}
            onChange={({ detail }) => setFieldValue(`steps[${index}].inspectedNote`, detail.value)}
          />
        </FormField>
        {step.type === StepTypes.INSPECTION && (
          <FormField label="Inspection Report">
            <FileUploader
              uploadPath={`orders/${orderId}/inspectionReport/${travelerId}/${index}`}
              uploadedFiles={step.inspectionReport || []}
              uploadErrors={errors?.inspectionReport || []}
              onEmpty={() => setFieldValue(`steps[${index}].inspectionReport`, [])}
              onUpload={(files) => setFieldValue(`steps[${index}].inspectionReport`, files)}
              fileTypeName="Inspection Report"
            />
          </FormField>
        )}
      </SpaceBetween>
    </ExpandableSection>
  );
}

TravelerStepCard.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  step: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  expanded: PropTypes.bool.isRequired,
  onExpandedChange: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  previousSteps: PropTypes.arrayOf(PropTypes.object).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  errors: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  pendingLots: PropTypes.arrayOf(PropTypes.object).isRequired,
  coverage: PropTypes.shape({ covered: PropTypes.bool }),
  // eslint-disable-next-line react/forbid-prop-types
  pParts: PropTypes.arrayOf(PropTypes.object),
  project: PropTypes.shape({ id: PropTypes.string }),
  projectLoading: PropTypes.bool,
  orderStepNote: PropTypes.string,
  orderId: PropTypes.string,
  travelerId: PropTypes.string.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  onPendingChange: PropTypes.func.isRequired,
  onCoverageChange: PropTypes.func.isRequired,
};
